/**
 * Roadmap #19 — camera-aware prompt suppression, client side.
 *
 * Cameras may ANSWER a location question OptiBay would otherwise ask; they are
 * never a prerequisite. The server (get_vehicle_location_assist) owns every
 * rule — freshness, camera health, eligible outcomes, Location Truth
 * precedence, holder / chain, conflicts, occupancy — and returns a DECISION.
 * This module only fetches it (bounded, never throwing) and maps it onto the
 * choices a surface already offers.
 *
 *   EXACT_CONFIDENT → "Found it in Bay 3" pre-answered, with Change
 *   SUGGESTION      → "Looks like Bay 3" highlighted; the human chooses
 *   NONE / failure  → the existing workflow, exactly as without cameras
 *
 * The answer is submitted through the SAME authority as a manual pick
 * (pull_in_and_start_my_timer / finish_my_work): the human workflow records
 * the location, never the camera. Browsers never read vehicle_observations.
 */
import { supabase } from '@/integrations/supabase/client';

export type LocationAssistContext = 'START' | 'STOP' | 'TEST_DRIVE_RETURN';

export interface LocationAssist {
  state: 'EXACT_CONFIDENT' | 'SUGGESTION' | 'NONE';
  kind: 'BAY' | 'PARKING_SPOT' | null;
  targetId: string | null;
  targetLabel: string | null;
  /** For tests and diagnostics only — never shown to a technician. */
  reason: string;
}

/** A Start / Stop never waits longer than this for camera help. */
export const LOCATION_ASSIST_TIMEOUT_MS = 1200;

export const NO_LOCATION_ASSIST: LocationAssist = Object.freeze({
  state: 'NONE', kind: null, targetId: null, targetLabel: null, reason: 'UNAVAILABLE',
}) as LocationAssist;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Anything unexpected is no assistance at all. */
export function parseLocationAssist(raw: unknown): LocationAssist {
  if (!raw || typeof raw !== 'object') return NO_LOCATION_ASSIST;
  const r = raw as Record<string, unknown>;
  const reason = typeof r.reason === 'string' ? r.reason : 'UNKNOWN';
  if (r.state !== 'EXACT_CONFIDENT' && r.state !== 'SUGGESTION') {
    return { ...NO_LOCATION_ASSIST, reason };
  }
  if ((r.kind !== 'BAY' && r.kind !== 'PARKING_SPOT')
      || typeof r.target_id !== 'string' || !UUID_RE.test(r.target_id)
      || typeof r.target_label !== 'string' || r.target_label.trim() === '') {
    return { ...NO_LOCATION_ASSIST, reason: 'MALFORMED' };
  }
  return { state: r.state, kind: r.kind, targetId: r.target_id, targetLabel: r.target_label, reason };
}

/**
 * One bounded read. Never throws, never blocks longer than the timeout:
 * failure, timeout or a malformed answer is simply "no assistance".
 */
export async function readLocationAssist(
  ticketId: string,
  context: LocationAssistContext,
  timeoutMs: number = LOCATION_ASSIST_TIMEOUT_MS,
): Promise<LocationAssist> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<LocationAssist>((resolve) => {
    timer = setTimeout(() => resolve({ ...NO_LOCATION_ASSIST, reason: 'TIMEOUT' }), timeoutMs);
  });
  const read = (async () => {
    try {
      const { data, error } = await supabase.rpc('get_vehicle_location_assist', { p_ticket_id: ticketId, p_context: context });
      if (error) return { ...NO_LOCATION_ASSIST, reason: 'ERROR' };
      return parseLocationAssist(data);
    } catch {
      return { ...NO_LOCATION_ASSIST, reason: 'ERROR' };
    }
  })();
  try {
    return await Promise.race([read, timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * The camera's answer to a question, restricted to what the surface offers:
 * the target must be one of the current choices, selectable (not occupied,
 * not refused in this act). Otherwise there is no answer — never a guess.
 */
export function assistAnswer<C extends { id: string; occupied?: boolean; refused?: boolean }>(
  assist: LocationAssist | null | undefined,
  kind: 'BAY' | 'PARKING_SPOT',
  choices: readonly C[],
  refusedIds: ReadonlySet<string> = new Set(),
): { found: C | null; suggested: C | null } {
  if (!assist || assist.state === 'NONE' || assist.kind !== kind || !assist.targetId) return { found: null, suggested: null };
  const choice = choices.find((c) => c.id === assist.targetId);
  if (!choice || choice.occupied || choice.refused || refusedIds.has(choice.id)) return { found: null, suggested: null };
  return assist.state === 'EXACT_CONFIDENT' ? { found: choice, suggested: null } : { found: null, suggested: choice };
}
